import {
  TextField,
  MenuItem,
  Typography,
  Paper,
  Grid,
  Autocomplete,
  Button,
  CircularProgress,
  Tooltip,
  IconButton,
  Box,
} from "@mui/material";
import MDButton from "components/MDButton";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import { useState } from "react";
import { addTrainingRecord } from "services/api";
import { fieldStyles } from "services/utils";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTranslation } from "react-i18next";

function TrainingRow({
  trainingSent,
  jobId,
  allComps,
  compToJobs,
  allTrainings,
  allLevels,
  employeeId,
  openSnack,
  onSave,
  saving,
  emp,
  onAchievedChange,
  onDelete,
  canEdit,
}) {
  const { t } = useTranslation();
  const [training, setTraining] = useState(
    trainingSent ?? {
      recordId: null,
      competencyId: "",
      trainingId: "",
      trainingName: "",
      startDate: "",
      endDate: "",
      expectedOutcomeLevel: "",
      achievedLevel: "",
    }
  );
  const isPast = dayjs(training?.endDate).isBefore(dayjs(), "day");

  const handleChange = (updates) => {
    setTraining((prev) => ({ ...prev, ...updates }));
  };

  const handleAchievedChange = async (e) => {
    const newLevel = e.target.value;

    await onAchievedChange(training, newLevel, employeeId, emp);
    handleChange({ achievedLevel: newLevel });
  };

  // 1. Find all compLevelIds for this job
  const jobCompLevelIds = compToJobs.filter((cj) => cj.jobId === jobId).map((cj) => cj.compLevelId);

  // 2. Resolve competencies via levels
  const jobCompetencyIds = [
    ...new Set(
      jobCompLevelIds
        .map((clId) => {
          const level = allLevels.find((lv) => lv.competencyLevelId === clId);
          return level ? level.competencyId : null;
        })
        .filter(Boolean)
    ),
  ];

  // 3. Get competency objects
  const jobCompetencies = allComps.filter((c) => jobCompetencyIds.includes(c.competencyId));

  // Trainings allowed for current competency
  const competencyTrainings = allTrainings.filter((tr) => tr.compId === training.competencyId);

  // Levels allowed for current competency
  const competencyLevels = allLevels.filter((lv) => lv.competencyId === training.competencyId);

  return (
    <Paper
      elevation={2}
      sx={{
        p: 2,
        mb: 2,
        borderRadius: 2,
        backgroundColor: isPast ? "grey.50" : "background.paper",
      }}
    >
      {isPast && training.recordId ? (
        <Grid container spacing={2} alignItems="center">
          {/* Training Name */}
          <Grid item xs={12} sm={3}>
            <Typography variant="subtitle2" color="text.secondary">
              {t("admin.trainingRow.title")}
            </Typography>
            <Tooltip
              title={allTrainings.find((tr) => tr.id === training.trainingId)?.trainingName || "-"}
              arrow
            >
              <Typography
                variant="body1"
                noWrap
                sx={{
                  maxWidth: 200, // adjust width as needed
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  display: "block",
                }}
              >
                {allTrainings.find((tr) => tr.id === training.trainingId)?.trainingName || "-"}
              </Typography>
            </Tooltip>
          </Grid>

          {/* Duration */}
          <Grid item xs={12} sm={3}>
            <Typography variant="subtitle2" color="text.secondary">
              {t("admin.trainingRow.duration")}
            </Typography>
            <Typography variant="body2" color="text.primary">
              {t("admin.trainingRow.start")}: {dayjs(training.startDate).format("DD-MM-YYYY")}
            </Typography>
            <Typography variant="body2" color="text.primary">
              {t("admin.trainingRow.end")}: {dayjs(training.endDate).format("DD-MM-YYYY")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t("admin.trainingRow.days", {
                count: dayjs(training.endDate).diff(dayjs(training.startDate), "days"),
              })}{" "}
              (
              {t("admin.trainingRow.weeks", {
                count: Math.ceil(
                  dayjs(training.endDate).diff(dayjs(training.startDate), "weeks", true)
                ),
              })}
              )
            </Typography>
          </Grid>

          {/* Expected */}
          <Grid item xs={12} sm={2.8}>
            <Typography variant="subtitle2" color="text.secondary">
              {t("admin.trainingRow.expected")}
            </Typography>
            <Tooltip
              title={
                allLevels.find((lvl) => lvl.competencyLevelId === training.expectedOutcomeLevel)
                  ?.levelName || "-"
              }
              arrow
            >
              <Typography
                variant="body1"
                noWrap
                sx={{
                  maxWidth: 200, // smaller width since levels are usually shorter
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  display: "block",
                }}
              >
                {allLevels.find((lvl) => lvl.competencyLevelId === training.expectedOutcomeLevel)
                  ?.levelName || "-"}
              </Typography>
            </Tooltip>
          </Grid>

          {/* Achieved level selector */}
          <Grid item xs={12} sm={2}>
            <TextField
              select
              size="small"
              fullWidth
              label={t("admin.trainingRow.achievedLevel")}
              disabled={!canEdit}
              value={training.achievedLevel || ""}
              sx={fieldStyles}
              onChange={handleAchievedChange}
            >
              {competencyLevels.map((lvl) => (
                <MenuItem key={lvl.competencyLevelId} value={lvl.competencyLevelId}>
                  {lvl.levelName}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          {training.recordId && canEdit && (
            <Grid item xs={12} sm="auto">
              <Tooltip title={t("admin.trainingRow.delete")}>
                <IconButton
                  color="error"
                  onClick={() => onDelete(training.recordId, emp)}
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          )}
        </Grid>
      ) : (
        <Grid container spacing={2} alignItems="center">
          {/* Competency */}
          <Grid item xs={9} sm={2}>
            {canEdit ? (
              <Autocomplete
                options={jobCompetencies}
                getOptionLabel={(option) => option?.name || ""}
                value={
                  jobCompetencies.find((c) => c.competencyId === training.competencyId) || null
                }
                onChange={(e, newVal) => handleChange({ competencyId: newVal?.competencyId || "" })}
                renderInput={(params) => (
                  <TextField {...params} size="small" label={t("admin.trainingRow.competency")} />
                )}
              />
            ) : (
              <Box>
                <Typography variant="caption" color="textSecondary">
                  {t("admin.trainingRow.competency")}
                </Typography>
                <Tooltip
                  title={
                    jobCompetencies.find((c) => c.competencyId === training.competencyId)?.name ||
                    ""
                  }
                >
                  <Typography noWrap sx={{ maxWidth: 160, textOverflow: "ellipsis" }}>
                    {jobCompetencies.find((c) => c.competencyId === training.competencyId)?.name ||
                      "-"}
                  </Typography>
                </Tooltip>
              </Box>
            )}
          </Grid>

          {/* Training */}
          <Grid item xs={9} sm={2}>
            {canEdit ? (
              <Autocomplete
                options={competencyTrainings}
                getOptionLabel={(option) => option?.trainingName || ""}
                value={competencyTrainings.find((tr) => tr.id === training.trainingId) || null}
                onChange={(e, newVal) => handleChange({ trainingId: newVal?.id || "" })}
                renderInput={(params) => (
                  <TextField {...params} size="small" label={t("admin.trainingRow.title")} />
                )}
              />
            ) : (
              <Box>
                <Typography variant="caption" color="textSecondary">
                  {t("admin.trainingRow.title")}
                </Typography>
                <Tooltip
                  title={
                    competencyTrainings.find((tr) => tr.id === training.trainingId)?.trainingName ||
                    ""
                  }
                >
                  <Typography noWrap sx={{ maxWidth: 160, textOverflow: "ellipsis" }}>
                    {competencyTrainings.find((tr) => tr.id === training.trainingId)
                      ?.trainingName || "-"}
                  </Typography>
                </Tooltip>
              </Box>
            )}
          </Grid>

          {/* Start Date */}
          <Grid item xs={12} sm={2}>
            {canEdit ? (
              <TextField
                type="date"
                size="small"
                fullWidth
                label={t("admin.trainingRow.startDate")}
                value={dayjs(training.startDate).format("YYYY-MM-DD")}
                onChange={(e) => handleChange({ startDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            ) : (
              <Box>
                <Typography variant="caption" color="textSecondary">
                  {t("admin.trainingRow.startDate")}
                </Typography>
                <Tooltip title={dayjs(training.startDate).format("YYYY-MM-DD")}>
                  <Typography noWrap>{dayjs(training.startDate).format("YYYY-MM-DD")}</Typography>
                </Tooltip>
              </Box>
            )}
          </Grid>

          {/* End Date */}
          <Grid item xs={12} sm={2}>
            {canEdit ? (
              <TextField
                type="date"
                size="small"
                fullWidth
                label={t("admin.trainingRow.endDate")}
                value={dayjs(training.endDate).format("YYYY-MM-DD")}
                onChange={(e) => handleChange({ endDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            ) : (
              <Box>
                <Typography variant="caption" color="textSecondary">
                  {t("admin.trainingRow.endDate")}
                </Typography>
                <Tooltip title={dayjs(training.endDate).format("YYYY-MM-DD")}>
                  <Typography noWrap>{dayjs(training.endDate).format("YYYY-MM-DD")}</Typography>
                </Tooltip>
              </Box>
            )}
          </Grid>

          {/* Expected */}
          <Grid item xs={12} sm={2}>
            {canEdit ? (
              <TextField
                select
                size="small"
                fullWidth
                label={t("admin.trainingRow.expected")}
                sx={fieldStyles}
                value={training.expectedOutcomeLevel || ""}
                onChange={(e) => handleChange({ expectedOutcomeLevel: e.target.value })}
              >
                {competencyLevels.map((lvl) => (
                  <MenuItem key={lvl.competencyLevelId} value={lvl.competencyLevelId}>
                    {lvl.levelName}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <Box>
                <Typography variant="caption" color="textSecondary">
                  {t("admin.trainingRow.expected")}
                </Typography>
                <Tooltip
                  title={
                    competencyLevels.find(
                      (lvl) => lvl.competencyLevelId === training.expectedOutcomeLevel
                    )?.levelName || ""
                  }
                >
                  <Typography noWrap sx={{ maxWidth: 160, textOverflow: "ellipsis" }}>
                    {competencyLevels.find(
                      (lvl) => lvl.competencyLevelId === training.expectedOutcomeLevel
                    )?.levelName || "-"}
                  </Typography>
                </Tooltip>
              </Box>
            )}
          </Grid>

          {/* Save / Delete only if canEdit */}
          {canEdit && (
            <>
              {saving ? (
                <Box display="flex" justifyContent="flex-end" p={1}>
                  <CircularProgress size={20} sx={{ color: "black" }} />
                </Box>
              ) : (
                <>
                  <Grid item xs={12} sm="auto">
                    <MDButton
                      variant="contained"
                      size="small"
                      color={training.recordId ? "info" : "primary"}
                      onClick={() => onSave(training, employeeId, emp)}
                      sx={{ textTransform: "none", borderRadius: 2, px: 2 }}
                    >
                      {t("admin.trainingRow.save")}
                    </MDButton>
                  </Grid>

                  {training.recordId && (
                    <Grid item xs={12} sm="auto">
                      <Tooltip title="Delete Training">
                        <IconButton
                          color="error"
                          onClick={() => onDelete(training.recordId, emp)}
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  )}
                </>
              )}
            </>
          )}
        </Grid>
      )}
    </Paper>
  );
}

TrainingRow.propTypes = {
  trainingSent: PropTypes.shape({
    recordId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    competencyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    trainingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    trainingName: PropTypes.string.isRequired,
    startDate: PropTypes.string.isRequired,
    endDate: PropTypes.string.isRequired,
    expectedOutcomeLevel: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    expectedLevelName: PropTypes.string,
    achievedLevelId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    achievedLevelName: PropTypes.string,
  }).isRequired,

  jobId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,

  allComps: PropTypes.arrayOf(
    PropTypes.shape({
      competencyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
      definition: PropTypes.string,
      type: PropTypes.string,
      status: PropTypes.string,
    })
  ).isRequired,

  compToJobs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      jobId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      compLevelId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      jobGroupId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ).isRequired,

  allTrainings: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      trainingName: PropTypes.string.isRequired,
      compId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    })
  ).isRequired,

  allLevels: PropTypes.arrayOf(
    PropTypes.shape({
      competencyLevelId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      competencyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      levelName: PropTypes.string.isRequired,
      value: PropTypes.number,
      startDate: PropTypes.string,
      endDate: PropTypes.string,
      definition: PropTypes.string,
    })
  ).isRequired,

  employeeId: PropTypes.string.isRequired,
  openSnack: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  saving: PropTypes.bool.isRequired,
  emp: PropTypes.object.isRequired,
  onAchievedChange: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  canEdit: PropTypes.bool.isRequired,
};

export default TrainingRow;
